import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react'

export type AuthenticatedUser = {
  id: string
  tenantId: string
  email: string
  name: string
  role: string
  timezone: string
  locale: string
}

type AuthState =
  | { status: 'loading'; user: null }
  | { status: 'authenticated'; user: AuthenticatedUser }
  | { status: 'anonymous'; user: null }

type AuthContextValue = AuthState & {
  login: (email: string, password: string) => Promise<{ ok: true } | { ok: false; error: string }>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null })

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' })
      if (res.ok) {
        const data = (await res.json()) as { user: AuthenticatedUser | null }
        if (data.user) {
          setState({ status: 'authenticated', user: data.user })
          return
        }
      }
    } catch {
      // ignore network errors → treat as anonymous
    }
    setState({ status: 'anonymous', user: null })
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const login = useCallback(async (email: string, password: string) => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      })
      if (!res.ok) {
        let msg = 'Credenziali non valide'
        try {
          const data = (await res.json()) as { error?: string }
          if (data.error) msg = data.error
        } catch {}
        return { ok: false as const, error: msg }
      }
      const data = (await res.json()) as { user: AuthenticatedUser }
      setState({ status: 'authenticated', user: data.user })
      return { ok: true as const }
    } catch (e) {
      return { ok: false as const, error: e instanceof Error ? e.message : 'Errore di rete' }
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    } catch {}
    setState({ status: 'anonymous', user: null })
  }, [])

  return <AuthContext.Provider value={{ ...state, login, logout }}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>')
  return ctx
}
